import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

/* ── static data matching screenshot ── */

const exposurePeriods = [
  { start: 0, width: 1.5 },
  { start: 3, width: 1 },
  { start: 5.5, width: 2 },
  { start: 9, width: 1.5 },
  { start: 12, width: 0.8 },
  { start: 14, width: 1.2 },
  { start: 17, width: 0.6 },
  { start: 19, width: 1.8 },
  { start: 22, width: 1 },
  { start: 25, width: 0.5 },
  { start: 27, width: 1.4 },
  { start: 30, width: 0.7 },
  { start: 33, width: 1.1 },
  { start: 36, width: 0.9 },
  { start: 39, width: 1.6 },
  { start: 42, width: 0.4 },
  { start: 44, width: 1.3 },
  { start: 47, width: 0.8 },
  { start: 50, width: 1 },
  { start: 53, width: 0.6 },
  { start: 56, width: 1.5 },
  { start: 59, width: 0.9 },
  { start: 62, width: 1.2 },
  { start: 65, width: 0.5 },
  { start: 68, width: 1.8 },
  { start: 72, width: 0.7 },
  { start: 75, width: 1.1 },
  { start: 78, width: 0.6 },
  { start: 81, width: 1.4 },
  { start: 85, width: 0.8 },
];

const densityData = [
  { period: '5%', density: 6 },
  { period: '10%', density: 4 },
  { period: '15%', density: 7 },
  { period: '20%', density: 3 },
  { period: '25%', density: 5 },
  { period: '30%', density: 8 },
  { period: '35%', density: 4 },
  { period: '40%', density: 6 },
  { period: '45%', density: 3 },
  { period: '50%', density: 7 },
  { period: '55%', density: 5 },
  { period: '60%', density: 4 },
  { period: '65%', density: 8 },
  { period: '70%', density: 6 },
  { period: '75%', density: 3 },
  { period: '80%', density: 5 },
  { period: '85%', density: 9 },
  { period: '90%', density: 7 },
  { period: '95%', density: 4 },
  { period: '100%', density: 6 },
];

const durationBuckets = [
  { name: '<1d', count: 0 },
  { name: '1-7d', count: 32 },
  { name: '1-4w', count: 26 },
  { name: '>1mo', count: 4 },
];

/* ── component ── */

export default function ExposureTimelineMock() {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
      }}
    >
      {/* header */}
      <div
        style={{
          background: '#111827',
          padding: '18px 24px 0',
        }}
      >
        <h3
          style={{
            color: '#f9fafb',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          Market Exposure Timeline
        </h3>
        <p
          style={{
            color: '#9ca3af',
            fontSize: 12,
            margin: '6px 0 16px',
          }}
        >
          Temporal structure of market participation across 62 positions
        </p>
      </div>

      {/* 2×2 grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          padding: '12px 24px 24px',
          background: '#111827',
        }}
      >
        {/* ── top-left: Exposure Timeline ── */}
        <Card title="Exposure Timeline" subtitle="In-market periods across the backtest duration">
          {/* timeline bar */}
          <div
            style={{
              position: 'relative',
              height: 32,
              background: '#374151',
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 8,
            }}
          >
            {exposurePeriods.map((p, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${p.start}%`,
                  width: `${p.width}%`,
                  top: 0,
                  bottom: 0,
                  background: 'rgba(52,211,153,0.8)',
                  borderRadius: 1,
                }}
              />
            ))}
          </div>

          {/* legend */}
          <div
            style={{
              display: 'flex',
              gap: 14,
              fontSize: 10,
              color: '#9ca3af',
              marginBottom: 14,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  background: 'rgba(52,211,153,0.8)',
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              />
              In market
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  background: '#4b5563',
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              />
              Out of market
            </span>
          </div>

          {/* metrics row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 8,
              borderTop: '1px solid #374151',
              paddingTop: 10,
            }}
          >
            <Metric label="Participation" value="9%" />
            <Metric label="Median hold" value="3.0d" />
            <Metric label="Longest gap" value="5.4mo" />
          </div>
        </Card>

        {/* ── top-right: Exposure Density ── */}
        <Card title="Exposure Density" subtitle="Rolling % time in market across test period">
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={densityData}
                margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 9, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                  interval={3}
                />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 9, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                  domain={[0, 100]}
                />
                <Area
                  type="monotone"
                  dataKey="density"
                  stroke="#64748B"
                  fill="#94A3B8"
                  fillOpacity={0.25}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#F9FAFB',
                  }}
                  labelFormatter={(v) => `${v} through test`}
                  formatter={(value: number) => [
                    `${value}% in market`,
                    'Density',
                  ]}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p
            style={{
              fontSize: 10,
              color: '#9ca3af',
              fontStyle: 'italic',
              margin: '8px 0 0',
            }}
          >
            Exposure varies across the test period
          </p>
        </Card>

        {/* ── bottom-left: Position Duration Distribution ── */}
        <Card
          title="Position Duration Distribution"
          subtitle="How long positions are typically held"
        >
          <div style={{ width: '100%', height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={durationBuckets}
                margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 9, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                />
                <Bar
                  dataKey="count"
                  fill="#FBBF24"
                  fillOpacity={0.7}
                  radius={[2, 2, 0, 0]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#F9FAFB',
                  }}
                  formatter={(value: number) => [
                    `${value} positions`,
                    'Count',
                  ]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* exposure profile */}
          <div
            style={{
              borderTop: '1px solid #374151',
              paddingTop: 10,
              marginTop: 10,
            }}
          >
            <p
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#9ca3af',
                margin: '0 0 8px',
              }}
            >
              Exposure Profile
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 16px',
                fontSize: 11,
              }}
            >
              <ProfileRow label="Participation" value="Low (9%)" />
              <ProfileRow label="Style" value="Multi-day positioning" />
              <ProfileRow label="Gap structure" value="Prolonged market absence observed" />
              <ProfileRow label="Load" value="Very low trade frequency" />
            </div>
          </div>
        </Card>

        {/* ── bottom-right: Observed Structure ── */}
        <Card title="Observed Structure" subtitle="">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#9ca3af',
                  margin: '0 0 6px',
                }}
              >
                Exposure Pattern
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Market participation is selective, with extended periods of
                inactivity. Capital is committed infrequently.
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#9ca3af',
                  margin: '0 0 6px',
                }}
              >
                Implications
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Strategy is highly selective. Performance depends on timing of
                specific entry conditions rather than market direction.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── helper components ── */

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: '#1f2937',
        borderRadius: 8,
        padding: 14,
        border: '1px solid #374151',
      }}
    >
      <h4
        style={{
          color: '#f9fafb',
          fontSize: 13,
          fontWeight: 700,
          margin: '0 0 2px',
        }}
      >
        {title}
      </h4>
      {subtitle && (
        <p
          style={{
            color: '#9ca3af',
            fontSize: 10,
            margin: '0 0 10px',
          }}
        >
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p style={{ fontSize: 10, color: '#9ca3af', margin: '0 0 2px' }}>
        {label}
      </p>
      <p
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#f9fafb',
          margin: 0,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span style={{ color: '#f9fafb', fontWeight: 600 }}>{value}</span>
    </div>
  );
}
